// src/components/AnswersCard.jsx
import React from "react";
import AnimatedNumber from "./AnimatedNumber";

/**
 * AnswersCard
 * ──────────────────────────────────────────────────────────────────────
 * Props:
 *   - slots: array of guess‐objects, each with:
 *       { guess: string, distance: number, bearing: number, percentage: number, isCorrect: boolean }
 *
 * Filters out “auto‐filled” correct guesses and displays one entry per guess.
 */
export default function AnswersCard({ slots = [] }) {
  // Only show real guesses (first correct guess) and all incorrect guesses
  const filteredSlots = slots.filter((slot) => {
    if (!slot) return false;
    if (slot.isCorrect && slot.guess !== "Auto-filled") return true;
    if (!slot.isCorrect) return true;
    return false;
  });

  return (
    <div
      className="answers-card"
      style={{
        display: "flex",
        flexDirection: "column-reverse",
        gap: "4px",
        margin: "8px 0",
        maxHeight: "300px",
        overflowY: "auto",
        width: "100%",
      }}
    >
      {filteredSlots.map((g, i) => {
        const animationDelay = `${(filteredSlots.length - 1 - i) * 0.2}s`;
        return (
          <div
            key={i}
            className="feedback-item"
            style={{
              animationDelay,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "4px",
              color: "#000",
              fontSize: "1rem",
              minHeight: "36px",
              height: "50px",
              width: "100%",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              margin: "4px 0",
              overflow: "hidden",
            }}
          >
            {/* Country Name */}
            <div
              className="fade-in-element"
              style={{
                width: "35%",
                padding: "0 8px",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 600,
                opacity: 0,
                animation: "fadeIn 1s forwards",
                animationDelay,
              }}
              title={g.guess || "N/A"}
            >
              {g.guess || "N/A"}
            </div>

            {/* Divider */}
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 4px",
              }}
            />

            {/* Distance (animated) */}
            <div
              style={{
                width: "20%",
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <AnimatedNumber value={g.distance || 0} duration={1000} /> km
            </div>

            {/* Divider */}
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 4px",
              }}
            />

            {/* Bearing arrow or 🎉 if correct */}
            <div
              className="fade-in-element"
              style={{
                width: "15%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                animation: "fadeIn 1s forwards",
                animationDelay,
              }}
            >
              {g.isCorrect ? (
                "🎉"
              ) : (
                <span
                  className="direction-arrow"
                  style={{
                    display: "inline-block",
                    transform: `rotate(${g.bearing}deg)`,
                    transformOrigin: "center",
                    transition: "transform 0.3s ease",
                    fontSize: "16px",
                  }}
                >
                  ↑
                </span>
              )}
            </div>

            {/* Divider */}
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 4px",
              }}
            />

            {/* Percentage (animated) */}
            <div
              style={{
                width: "15%",
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <AnimatedNumber
                value={g.isCorrect ? 100 : g.percentage || 0}
                duration={1000}
              />{" "}
              %
            </div>
          </div>
        );
      })}
    </div>
  );
}
